"use client";

import React, { useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAPYContext } from "@/features/apy";
import styles from "./FeatureBeacon.module.css";

interface Feature {
  id: string;
  icon: React.ReactNode;
  label: string;
  highlight?: string;
  accentColor: string;
  glowColor: string;
}

export default function FeatureBeacon() {
  const { apyRate } = useAPYContext();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const progressRef = useRef<SVGCircleElement>(null);

  const features: Feature[] = [
    {
      id: "savings",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className={styles.featureIcon}>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          <path
            d="M12 6V18M15 9.5C15 8.12 13.66 7 12 7C10.34 7 9 8.12 9 9.5C9 10.88 10.34 12 12 12C13.66 12 15 13.12 15 14.5C15 15.88 13.66 17 12 17C10.34 17 9 15.88 9 14.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      label: "High Yield Savings",
      highlight: apyRate ? `${apyRate}% APY` : undefined,
      accentColor: "#FFD700",
      glowColor: "rgba(255, 215, 0, 0.4)",
    },
    {
      id: "investing",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className={styles.featureIcon}>
          <path
            d="M3 17L9 11L13 15L21 7"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17 7H21V11"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      label: "U.S. Stock Market Investing",
      accentColor: "#00D4AA",
      glowColor: "rgba(0, 212, 170, 0.4)",
    },
    {
      id: "gamified",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className={styles.featureIcon}>
          <path
            d="M12 15C15.866 15 19 11.866 19 8C19 4.13401 15.866 1 12 1C8.13401 1 5 4.13401 5 8C5 11.866 8.13401 15 12 15Z"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      label: "Gamified Finance",
      accentColor: "#FF6B9D",
      glowColor: "rgba(255, 107, 157, 0.4)",
    },
  ];

  const nextFeature = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % features.length);
    setAnimationKey((prev) => prev + 1);
  }, [features.length]);

  // Handle animation end - triggered by CSS animation completing
  const handleAnimationEnd = useCallback(() => {
    if (!isPaused) {
      nextFeature();
    }
  }, [isPaused, nextFeature]);

  const currentFeature = features[activeIndex];

  return (
    <motion.div
      className={styles.beaconWrapper}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Ambient glow */}
      <motion.div
        className={styles.ambientGlow}
        animate={{
          background: `radial-gradient(ellipse at center, ${currentFeature.glowColor} 0%, transparent 70%)`,
        }}
        transition={{ duration: 0.5 }}
      />

      {/* Main beacon container */}
      <div className={styles.beaconContainer}>
        {/* Progress ring - CSS animated */}
        <svg className={styles.progressRing} viewBox="0 0 100 100">
          <circle
            className={styles.progressTrack}
            cx="50"
            cy="50"
            r="47"
            style={{
              stroke: currentFeature.accentColor,
            }}
          />
          <circle
            ref={progressRef}
            key={animationKey}
            className={`${styles.progressFill} ${isPaused ? styles.progressPaused : ""}`}
            cx="50"
            cy="50"
            r="47"
            style={{
              stroke: currentFeature.accentColor,
            }}
            onAnimationEnd={handleAnimationEnd}
          />
        </svg>

        {/* Content area */}
        <div className={styles.contentArea}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentFeature.id}
              className={styles.featureContent}
              initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 1.1, filter: "blur(10px)" }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            >
              <motion.div
                className={styles.iconWrapper}
                style={{ color: currentFeature.accentColor }}
                animate={{
                  boxShadow: `0 0 30px ${currentFeature.glowColor}`,
                }}
              >
                {currentFeature.icon}
              </motion.div>

              <div className={styles.textContent}>
                <span className={styles.featureLabel}>
                  {currentFeature.label}
                  {currentFeature.highlight && (
                    <>
                      {" "}
                      <motion.span
                        className={styles.featureHighlight}
                        style={{
                          background: `linear-gradient(90deg, ${currentFeature.accentColor}, #fff, ${currentFeature.accentColor})`,
                          backgroundSize: "200% auto",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }}
                        animate={{
                          backgroundPosition: ["0% center", "200% center"],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        {currentFeature.highlight}
                      </motion.span>
                    </>
                  )}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation dots */}
        <div className={styles.navDots}>
          {features.map((feature, index) => (
            <button
              key={feature.id}
              className={`${styles.navDot} ${index === activeIndex ? styles.navDotActive : ""}`}
              onClick={() => {
                setActiveIndex(index);
                setAnimationKey((prev) => prev + 1);
              }}
              aria-label={`Show ${feature.label}`}
              style={{
                backgroundColor: index === activeIndex ? feature.accentColor : undefined,
                boxShadow: index === activeIndex ? `0 0 12px ${feature.accentColor}` : undefined,
              }}
            />
          ))}
        </div>
      </div>

      {/* Decorative particles */}
      <div className={styles.particles}>
        {[...Array(6)].map((_, i) => (
          <motion.span
            key={i}
            className={styles.particle}
            style={{
              backgroundColor: currentFeature.accentColor,
              left: `${15 + i * 14}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 2 + i * 0.3,
              repeat: Infinity,
              delay: i * 0.2,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>
    </motion.div>
  );
}
